import { AppUserDataLoader } from "@/data/dataLoader"
import { useAppStore } from "@/stores/AppStoreProvider"

export const useCustomerProfile = <R extends boolean = true>({ required = true as R }: { required?: R } = {}) => {
  const profiles = useAppStore((state) => state.profiles)
  if (required && !profiles.customer) {
    throw new Error("Customer profile not found")
  }
  return {
    customerProfile: profiles.customer,
  } as {
    customerProfile: R extends true ? NonNullable<typeof profiles.customer> : typeof profiles.customer
  }
}

export const useMerchantProfile = <R extends boolean = true>({ required = true as R }: { required?: R } = {}) => {
  const profiles = useAppStore((state) => state.profiles)
  if (required && !profiles.merchant) {
    throw new Error("Merchant profile not found")
  }
  return {
    merchantProfile: profiles.merchant,
  } as {
    merchantProfile: R extends true ? NonNullable<typeof profiles.merchant> : typeof profiles.merchant
  }
}

export const useMerchantCouponRedeemer = <R extends boolean = true>({
  required = true as R,
}: { required?: R } = {}) => {
  const profiles = useAppStore((state) => state.profiles)
  if (required && !profiles.merchantCouponRedeemer) {
    throw new Error("Merchant coupon redeemer profile not found")
  }
  return {
    merchantCouponRedeemer: profiles.merchantCouponRedeemer,
  } as {
    merchantCouponRedeemer: R extends true
      ? NonNullable<typeof profiles.merchantCouponRedeemer>
      : typeof profiles.merchantCouponRedeemer
  }
}

export const useProgressBar = () => {
  const isProgressVisible = useAppStore((state) => state.isProgressVisible)
  const setProgressVisible = useAppStore((state) => state.setProgressVisible)
  const progress = useAppStore((state) => state.progress)

  return {
    isVisible: isProgressVisible,
    progress,
    startProgress: () => setProgressVisible(0, true),
    completeProgress: () => setProgressVisible(100, true),
    setProgress: (progress: number) => setProgressVisible(progress, true),
  }
}

export const useAppUser = <T extends AppUserDataLoader | null = AppUserDataLoader>({
  required = true,
}: { required?: boolean } = {}) => {
  const appUser = useAppStore((state) => state.appUser)
  if (required && !appUser) {
    throw new Error("App user not found")
  }
  return {
    appUser: required ? (appUser as AppUserDataLoader) : (appUser as T),
  }
}

export const useIsMobile = () => {
  return useAppStore((state) => state.isMobile)
}

export const useImpersonationJwt = () => {
  return useAppStore((state) => state.impersonationJwt)
}
